import { BaseModel, type RelationConfig } from './base';
import { dateYMDRelation, timeMinuteRelation, dateRelation, type DateYMD, type TimeMinute } from '../lib/dates';
import { plugin as $date } from '~/plugins/date';

import { Customer } from './customer';
import { CustomerPass } from './customerPass';
import { CustomField } from './customField';
import { Employee } from './employee';
import { Recurrence } from './recurrence';
import { ReservationCustomer } from './reservationCustomer';
import { ReservationServiceModel } from './reservationServiceModel';
import { ReservationStatusType } from './reservationStatusType';
import { Service, ServiceCustomerType } from './service';
import { ServiceResource } from './serviceResource';
import { ServiceForm } from './serviceForm';
import { Voucher } from './voucher';

// Enums

export enum ReservationServiceType {
  single = 0,
  multi = 1,
}

export const ReservationServiceTypeLabels: Record<ReservationServiceType, string> = {
  [ReservationServiceType.single]: 'general.single',
  [ReservationServiceType.multi]: 'api.models.overviewBlock.multi',
};

export enum ReservationScheduleType {
  standard = 0,
  timetable = 1,
}

export const ReservationScheduleTypeLabels: Record<ReservationScheduleType, string> = {
  [ReservationScheduleType.standard]: 'reservation.standard',
  [ReservationScheduleType.timetable]: 'reservation.timetable',
};

export enum ReservationStatus {
  unconfirmed = 0,
  confirmed = 1,
  cancelled = 2,
}

export const ReservationStatusLabels: Record<ReservationStatus, string> = {
  [ReservationStatus.unconfirmed]: 'reservation.unconfirmed',
  [ReservationStatus.confirmed]: 'reservation.confirmed',
  [ReservationStatus.cancelled]: 'general.cancelled',
};

export enum ReservationCustomerRequestType {
  none = 0,
  request = 1,
  newClient = 2,
  newClientRequest = 3,
  transient = 4,
}

export const ReservationCustomerRequestTypeLabels: Record<ReservationCustomerRequestType, string> = {
  [ReservationCustomerRequestType.none]: 'general.none',
  [ReservationCustomerRequestType.request]: 'reservation.existingEmployeeRequested',
  [ReservationCustomerRequestType.newClient]: 'reservation.newAnyEmployee',
  [ReservationCustomerRequestType.newClientRequest]: 'reservation.newEmployeeRequested',
  [ReservationCustomerRequestType.transient]: 'reservation.existingAnyEmployee',
};

export enum ReservationEmployeeFunction {
  none = 0,
  employee = 1,
  resource = 2,
}

export const ReservationEmployeeFunctionLabels: Record<ReservationEmployeeFunction, string> = {
  [ReservationEmployeeFunction.none]: 'general.none',
  [ReservationEmployeeFunction.employee]: 'general.employee',
  [ReservationEmployeeFunction.resource]: 'general.resource',
};

export enum ReservationEmailType {
  received = 'received',
  confirmation = 'confirmation',
  reminderEmail = 'reminderEmail',
  reminderSMS = 'reminderSMS',
  reviewRequest = 'reviewRequest',
}

export const ReservationEmailTypeLabels: Record<ReservationEmailType, string> = {
  [ReservationEmailType.received]: 'general.received',
  [ReservationEmailType.confirmation]: 'general.confirmation',
  [ReservationEmailType.reminderEmail]: 'reservation.reminderEmail2',
  [ReservationEmailType.reminderSMS]: 'reservation.reminderSMS2',
  [ReservationEmailType.reviewRequest]: 'reservation.reviewRequest',
};

export enum ReservationServiceAutomationState {
  queued = 0,
  processed = 1,
  skipped = 2,
  failed = 3,
}

export const ReservationServiceAutomationStateLabels: Record<ReservationServiceAutomationState, string> = {
  [ReservationServiceAutomationState.queued]: 'general.queued',
  [ReservationServiceAutomationState.processed]: 'general.processed',
  [ReservationServiceAutomationState.skipped]: 'general.skipped',
  [ReservationServiceAutomationState.failed]: 'general.failed',
};

// Helper Classes

export class ReservationServiceAutomation extends BaseModel {
  state!: ReservationServiceAutomationState;

  get isStateQueued(): boolean {
    return this.state === ReservationServiceAutomationState.queued;
  }

  get isStateProcessed(): boolean {
    return this.state === ReservationServiceAutomationState.processed;
  }

  get isStateSkipped(): boolean {
    return this.state === ReservationServiceAutomationState.skipped;
  }

  get isStateFailed(): boolean {
    return this.state === ReservationServiceAutomationState.failed;
  }
}

export class ReservationAutomation extends BaseModel {
  serviceAutomation!: any;
  reservationServiceAutomation!: ReservationServiceAutomation;

  static override relations(): RelationConfig {
    return {
      reservationServiceAutomation: { type: ReservationServiceAutomation },
    };
  }
}

export class ReservationOptions extends BaseModel {
  statusTypes!: ReservationStatusType[];
  services!: ReservationServiceOptions[];

  static override relations(): RelationConfig {
    return {
      services: { type: ReservationServiceOptions },
      statusTypes: { type: ReservationStatusType },
    };
  }
}

export class ReservationServiceOptions extends BaseModel {
  customerPasses!: CustomerPass[];
  vouchers!: Voucher[];

  static override relations(): RelationConfig {
    return {
      customerPasses: { type: CustomerPass },
      vouchers: { type: Voucher },
    };
  }
}

export class ReservationRecurOptions extends BaseModel {
  types!: ReservationRecurType[];

  static override relations(): RelationConfig {
    return {
      types: { type: ReservationRecurType },
    };
  }
}

export class ReservationRecurType extends BaseModel {
  options!: ReservationRecurTypeOption[];

  static override relations(): RelationConfig {
    return {
      options: { type: ReservationRecurTypeOption },
    };
  }
}

export class ReservationRecurTypeOption extends BaseModel {}
export class ReservationRecurRequest extends BaseModel {}
export class ReservationRecurResponse extends BaseModel {}
export class ReservationFrontDesk extends BaseModel {}

export class ReservationAttendees extends BaseModel {
  status!: ReservationStatus;

  get isStatusConfirmed(): boolean {
    return this.status === ReservationStatus.confirmed;
  }

  get isStatusUnconfirmed(): boolean {
    return this.status === ReservationStatus.unconfirmed;
  }
}

export class ReservationRelatedForm extends BaseModel {
  serviceForm!: ServiceForm | null;

  static override relations(): RelationConfig {
    return {
      serviceForm: { type: ServiceForm },
    };
  }
}

export class ReservationResource extends BaseModel {
  serviceResource!: ServiceResource | null;
  resource!: Employee | null;

  static override relations(): RelationConfig {
    return {
      serviceResource: { type: ServiceResource },
      resource: { type: Employee },
    };
  }
}

export class ReservationCustomerResource extends BaseModel {
  serviceResource!: ServiceResource | null;
  resource!: Employee | null;
  customer!: Customer | null;

  static override relations(): RelationConfig {
    return {
      serviceResource: { type: ServiceResource },
      resource: { type: Employee },
      customer: { type: Customer },
    };
  }
}

export class ReservationIntegration extends BaseModel {}

export class ReservationCustomerRecurrencePreview extends BaseModel {}

interface ReservationApiComputedValues {
  servicesLabel?: string;
  startTime?: number;
  finishTime?: number;
}

const apiComputedValues = Symbol('reservationApiComputedValues');

function readApiComputedValues(reservation: object): ReservationApiComputedValues | undefined {
  return (reservation as Record<symbol, ReservationApiComputedValues | undefined>)[apiComputedValues];
}

function getApiComputedValues(reservation: object): ReservationApiComputedValues {
  const existing = readApiComputedValues(reservation);
  if (existing) return existing;

  const values: ReservationApiComputedValues = {};
  Object.defineProperty(reservation, apiComputedValues, {
    configurable: true,
    enumerable: false,
    value: values,
  });
  return values;
}

// Main Model

export class Reservation extends BaseModel {
  idReservation!: number;
  date!: DateYMD | null;
  startTime!: TimeMinute | null;
  finishTime!: number | null;
  startTimeUTC!: Date | null;
  finishTimeUTC!: Date | null;
  duration!: number;
  infoLabel!: string;
  servicesLabel!: string;
  recurrenceGuid!: string | null;
  recurrence!: Recurrence | null;
  status!: ReservationStatus;
  serviceType!: ReservationServiceType;
  scheduleType!: ReservationScheduleType;
  customerType!: ServiceCustomerType;
  customerRequestType!: ReservationCustomerRequestType;
  customer!: Customer | null;
  employee!: Employee | null;
  service!: Service | null;
  services!: ReservationServiceModel[];
  customFields!: CustomField[];
  options!: ReservationOptions | null;
  statusType!: ReservationStatusType | null;
  attendees!: ReservationAttendees | null;
  groupCustomers!: ReservationCustomer[];
  relatedForms!: ReservationRelatedForm[];
  automations!: ReservationAutomation[];
  integrations!: ReservationIntegration[];
  voucher!: Voucher | null;
  saturated!: Date | null;
  checkedIn!: boolean;
  didNotShow_!: boolean;
  isDeleted!: boolean;
  cancelled!: boolean;
  overrideTime!: boolean;

  // Identifier used by order-item payloads ($api.order.create) and by the
  // $bus model-event channel. Ported from the Nuxt 2 `static modelName()` on
  // each model class. Called as a method (not a property) to match existing
  // callsites — see app/composables/useOrderCreate.ts and
  // app/components/order/actions/CreateOrder.vue.
  static modelName(): string {
    return 'reservation'
  }

  static override relations(): RelationConfig {
    return {
      customer: { type: Customer },
      customFields: { type: CustomField },
      date: dateYMDRelation,
      employee: { type: Employee },
      recurrence: { type: Recurrence },
      services: { type: ReservationServiceModel },
      startTime: timeMinuteRelation,
      startTimeUTC: dateRelation,
      finishTimeUTC: dateRelation,
      options: { type: ReservationOptions },
      service: { type: Service },
      statusType: { type: ReservationStatusType },
      attendees: { type: ReservationAttendees },
      groupCustomers: { type: ReservationCustomer },
      relatedForms: { type: ReservationRelatedForm },
      automations: { type: ReservationAutomation },
      integrations: { type: ReservationIntegration },
      voucher: { type: Voucher },
    };
  }

  override objectID(): string | null {
    return this.idReservation?.toString() ?? null;
  }

  override toJSON(): Record<string, any> {
    return {
      ...super.toJSON(),
      overrideTime: this.overrideTime ?? false,
    };
  }

  override get display(): string {
    return this.infoLabel;
  }

  get isRecurring(): boolean {
    return !!this.recurrenceGuid;
  }

  get isMultiService(): boolean {
    return this.serviceType === ReservationServiceType.multi;
  }

  get isGroup(): boolean {
    return this.customerType === ServiceCustomerType.group;
  }

  get isSingle(): boolean {
    return this.customerType === ServiceCustomerType.single;
  }

  get isStandardSchedule(): boolean {
    return this.scheduleType === ReservationScheduleType.standard;
  }

  get isTimetableSchedule(): boolean {
    return this.scheduleType === ReservationScheduleType.timetable;
  }

  get isStatusConfirmed(): boolean {
    return this.status === ReservationStatus.confirmed;
  }

  get isStatusUnconfirmed(): boolean {
    return this.status === ReservationStatus.unconfirmed;
  }

  get isStatusCancelled(): boolean {
    return this.status === ReservationStatus.cancelled;
  }

  get hasVoucher(): boolean {
    if (this.voucher && Object.keys(this.voucher).length > 0) return true;
    return this.services?.some(service => !!service.voucher && Object.keys(service.voucher).length > 0) ?? false;
  }

  get hasPass(): boolean {
    return !!(this as any).customerPass || this.services?.some(service => !!service.customerPass) || false;
  }

  get _servicesLabel(): string {
    const apiLabel = readApiComputedValues(this)?.servicesLabel;
    if (apiLabel != null) return apiLabel;
    if (this.servicesLabel) return this.servicesLabel;

    return this.services
      ?.map(service => service.service?.title || service.title)
      .filter(Boolean)
      .join(', ') ?? '';
  }

  set _servicesLabel(value: string | null | undefined) {
    getApiComputedValues(this).servicesLabel = value ?? undefined;
  }

  get _startTime(): number | null {
    const apiStartTime = readApiComputedValues(this)?.startTime;
    if (apiStartTime != null) return apiStartTime;
    if (this.startTime != null) return Number(this.startTime);
    return this.services?.[0]?.startTime ?? null;
  }

  set _startTime(value: number | string | null | undefined) {
    getApiComputedValues(this).startTime = value == null ? undefined : Number(value);
  }

  get _finishTime(): number | null {
    const apiFinishTime = readApiComputedValues(this)?.finishTime;
    if (apiFinishTime != null) return apiFinishTime;
    if (this.finishTime != null) return this.finishTime;

    const lastService = this.services?.at(-1);
    if (!lastService) return null;

    const finishTime = (lastService as any).finishTime;
    if (finishTime != null) return finishTime;

    return lastService.startTime + lastService.duration;
  }

  set _finishTime(value: number | string | null | undefined) {
    getApiComputedValues(this).finishTime = value == null ? undefined : Number(value);
  }

  get employeesLabel(): string {
    if (this.services?.length) {
      const employees = this.services
        .map((s: any) => s.employee?.displayName)
        .filter(Boolean);
      return [...new Set(employees)].join(', ');
    }
    return this.employee?.displayName || '';
  }

  get durationLabel(): number {
    if (!this.services?.length) return 0;
    return this.services.reduce((sum: number, s: any) => sum + (s.duration || 0), 0);
  }

  get startLabel(): string | null {
    const utc = $date.utc(this.startTimeUTC);
    return $date.format(utc, $date.presets.time);
  }

  get finishLabel(): string | null {
    const utc = $date.utc(this.finishTimeUTC);
    return $date.format(utc, $date.presets.time);
  }
}
